/**
 * plot.js — turning a curve spec into screen points.
 *
 * ── NO LIBRARY, AND THE VOCABULARY IS WHY ────────────────────────────────────
 *
 * A plotting library earns its place when arbitrary EXPRESSIONS must be
 * rendered: you need a parser, an AST and a compiled evaluator. The closed
 * vocabulary removed that need at the same time it removed the library —
 * `{"type":"quadratic","a":1,"b":0,"c":-4}` is `x => x*x - 4`, one line.
 *
 * ── BRANCH ON HOW A CURVE IS SAMPLED, NOT ON WHICH TYPE IT IS ────────────────
 *
 * Nineteen types would be nineteen branches. THREE SAMPLING KINDS is three, and
 * every type falls into one:
 *
 *   y = f(x)      line, quadratic, cubic, polynomial, exponential, logarithmic,
 *                 sine, tangent, abs, sqrt, rational, step, constant
 *   parametric    circle, ellipse, verticalLine, polar   (none is a function)
 *   given points  polyline   (no sampling at all)
 *
 * PIECEWISE IS NOT A FOURTH BRANCH. It is a curve that yields SEVERAL
 * intervals, each going to whichever sampler its own curve needs — so the
 * sampler never learns that piecewise exists, and a piecewise can mix a line, a
 * parabola and a polar arc without any special case.
 *
 * Adding `hyperbola` or `cosh` later is one line in a table. Never a branch.
 *
 * ── PURE ────────────────────────────────────────────────────────────────────
 *
 * No React, no imports, no DOM. Every function here is testable on its own, and
 * the module is reusable for drawing ANSWERS later — "sketch y = x^2 - 4" can
 * show its result through exactly this code.
 */

/**
 * ── EVERY TYPE THAT IS A FUNCTION OF x ──────────────────────────────────────
 *
 * KEEP THIS IN STEP WITH `TYPES` IN services/graphReader.js. A type the reader
 * accepts but this module cannot draw renders as NOTHING — empty axes shown as
 * though they were the answer, which is worse than a dashed box saying we could
 * not draw it.
 *
 * A MISSING COEFFICIENT NEEDS NO GUARD. It becomes NaN at evaluation, and the
 * sampler breaks the path on NaN by design, so the curve simply does not draw.
 * Likewise `sqrt` of a negative and `log` of a non-positive: undefined regions
 * handle themselves.
 */
export const CURVE = {
  line:        (c) => (x) => c.m * x + c.b,
  quadratic:   (c) => (x) => c.a * x * x + c.b * x + c.c,
  cubic:       (c) => (x) => c.a * x ** 3 + c.b * x * x + c.c * x + c.d,
  polynomial:  (c) => (x) => (c.coeffs || []).reduce((s, k, i) => s + k * x ** i, 0),
  exponential: (c) => (x) => c.a * Math.exp(c.k * x) + (c.shift || 0),
  logarithmic: (c) => (x) => c.a * Math.log(x - c.h) + c.k,
  sine:        (c) => (x) => c.amplitude * Math.sin(2 * Math.PI * (x - c.phase) / c.period) + c.shift,
  tangent:     (c) => (x) => c.a * Math.tan(2 * Math.PI * (x - c.phase) / c.period) + c.shift,
  abs:         (c) => (x) => c.a * Math.abs(x - c.h) + c.k,
  sqrt:        (c) => (x) => c.a * Math.sqrt(x - c.h) + c.k,
  step:        (c) => (x) => c.a * Math.floor((x - c.h) / c.width) + c.k,
  constant:    (c) => () => c.y,
  rational:    (c) => (x) => {
    const poly = (ks) => (ks || []).reduce((s, k, i) => s + k * x ** i, 0);
    // `num` and `den` are coefficient lists, but the model has also been seen to
    // send a plain number for `num` with a single pole in `den`. Accept both
    // rather than lose the curve over a shape detail.
    const n = Array.isArray(c.num) ? poly(c.num) : c.num;
    const d = Array.isArray(c.den) ? poly(c.den) : (x - c.pole);
    return n / d;
  },
};

/** ── POLAR SHAPES. r as a function of the angle. ───────────────────────────── */
export const POLAR = {
  rose:     (c) => (t) => c.a * Math.cos((c.petals || 2) * t),
  cardioid: (c) => (t) => c.a * (1 + Math.cos(t)),
  spiral:   (c) => (t) => c.a * t,
  circle:   (c) => () => c.a,
  limacon:  (c) => (t) => c.a + (c.b || 0) * Math.cos(t),
};

/**
 * ── EVERY TYPE THAT IS NOT A FUNCTION OF x ──────────────────────────────────
 *
 * A circle has two y for one x, so `y = f(x)` cannot draw it. Each of these
 * yields a POINT per sample of `t`.
 *
 * WHY NOT TWO `sqrt` HALVES for a circle: they meet at a VERTICAL TANGENT,
 * where sampling gets arbitrarily close and never arrives. Measured — the top
 * half of r = 5 stops at x = 4.9985, leaving a visible notch at each side. A
 * parametric path closes exactly.
 *
 * POLAR RIDES THE SAME PATH WITH NOTHING NEW: r = f(theta) plotted as
 * (r cos t, r sin t).
 */
export const PARAM = {
  circle:  (c) => ({ from: 0, to: 2 * Math.PI, closed: true,
    at: (t) => [c.h + c.r * Math.cos(t), c.k + c.r * Math.sin(t)] }),
  ellipse: (c) => ({ from: 0, to: 2 * Math.PI, closed: true,
    at: (t) => [c.h + c.a * Math.cos(t), c.k + c.b * Math.sin(t)] }),
  polar:   (c) => ({ from: c.from ?? 0, to: c.to ?? 2 * Math.PI, closed: false,
    at: (t) => {
      const r = (POLAR[c.shape] || POLAR.circle)(c)(t);
      return [r * Math.cos(t), r * Math.sin(t)];
    } }),
  // A vertical line is the degenerate case: t runs over y.
  verticalLine: (c) => ({ from: 0, to: 1, closed: false, at: null, x: c.x }),
};

/**
 * The screen mapping for a window.
 *
 * `window` is [xmin, ymax, xmax, ymin] — JSXGraph's boundingbox order, kept
 * because the graph-reading prompt asks for it in that order. Confusing enough
 * that it is converted here, once, and nothing downstream sees it again.
 */
/**
 * A ROUND TICK SPACING NEAR A TARGET.
 *
 * `(max - min) / 10` is the right IDEA and the wrong NUMBER. On a window
 * spanning 11 it gives 1.1, and the gridlines land at -4.4, -3.3, -2.2 — while
 * the page the student is holding has them at whole numbers.
 *
 * A graph's gridlines should sit where a person would draw them, so the target
 * is snapped to 1, 2 or 5 times a power of ten. Span 11 gives a step of 1;
 * span 0.7 gives 0.1; span 850 gives 100.
 */
function niceStep(target) {
  if (!Number.isFinite(target) || target <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(target));
  const norm = target / mag;                       // 1 <= norm < 10
  const step = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
  return step * mag;
}

export function viewport(window, width, height) {
  const [xmin, ymax, xmax, ymin] = window;
  return {
    xmin, xmax, ymin, ymax, width, height,
    sx: (x) => ((x - xmin) / (xmax - xmin)) * width,
    sy: (y) => ((ymax - y) / (ymax - ymin)) * height,
    ix: (px) => xmin + (px / width) * (xmax - xmin),      // screen -> data
    // TICK SPACING IS DERIVED, NOT STORED. One rule, no field, nothing for the
    // model to get wrong — and then SNAPPED TO A ROUND NUMBER.
    stepX: niceStep((xmax - xmin) / 10),
    stepY: niceStep((ymax - ymin) / 10),
  };
}

/**
 * ── A CURVE YIELDS INTERVALS ────────────────────────────────────────────────
 *
 * Ordinary curves yield one, spanning the window. PIECEWISE YIELDS SEVERAL,
 * each with its own function — which is the whole reason the sampler needs no
 * knowledge of piecewise.
 *
 * A piece may name only part of the window, or run past its edges; both are
 * clamped. A piece entirely outside the window yields nothing.
 */
export function intervals(curve, vp) {
  if (!curve || typeof curve !== 'object') return [];
  if (curve.type !== 'piecewise') {
    const fn = CURVE[curve.type];
    return fn ? [{ fn: fn(curve), x0: vp.xmin, x1: vp.xmax, curve }] : [];
  }
  return (curve.pieces || [])
    .filter((p) => p && p.curve && CURVE[p.curve.type])
    .map((p) => ({
      fn: CURVE[p.curve.type](p.curve),
      x0: p.from == null ? vp.xmin : Math.max(vp.xmin, p.from),
      x1: p.to == null ? vp.xmax : Math.min(vp.xmax, p.to),
      piece: p,
      curve: p.curve,
    }))
    .filter((s) => s.x1 > s.x0);
}

/**
 * ── A PIECE THAT IS NOT A FUNCTION OF x ─────────────────────────────────────
 *
 * `intervals` only knows the `y = f(x)` table, so a piece holding a POLYLINE, a
 * circle or a polar arc was silently DROPPED — and a piecewise made entirely of
 * polylines drew nothing at all, which is exactly what shipped.
 *
 * `graphReader` accepted those specs because `polyline` is a drawable type; the
 * two disagreed about where "drawable" is decided. **It is decided in ONE place
 * now: `curveRuns`.** A piece is just a curve with an interval, so it goes
 * through the same dispatch a top-level curve does.
 *
 * THE INTERVAL IS APPLIED AFTERWARDS, by clipping the returned points to the
 * piece's x range. A polyline already carries its own extent, so this only
 * matters when a piece names a narrower one than its points cover.
 */
function nonFunctionPieces(curve, vp, opts) {
  if (!curve || curve.type !== 'piecewise') return [];
  const out = [];
  for (const p of curve.pieces || []) {
    const c = p && p.curve;
    if (!c || CURVE[c.type]) continue;                  // handled by `intervals`
    const x0 = p.from == null ? vp.xmin : Math.max(vp.xmin, p.from);
    const x1 = p.to == null ? vp.xmax : Math.min(vp.xmax, p.to);
    if (!(x1 > x0)) continue;
    const lo = vp.sx(x0);
    const hi = vp.sx(x1);
    for (const run of curveRuns(c, vp, opts)) {
      // KEEP A POINT ONLY IF IT LIES IN THIS PIECE'S RANGE. A run that leaves
      // the range and comes back is split, which is the same thing the sampler
      // does at a pole.
      let part = [];
      for (const q of run) {
        if (q[0] >= lo - 0.01 && q[0] <= hi + 0.01) { part.push(q); continue; }
        if (part.length > 1) out.push(part);
        part = [];
      }
      if (part.length > 1) out.push(part);
    }
  }
  return out;
}

/**
 * ── SAMPLING y = f(x) ───────────────────────────────────────────────────────
 *
 * Four findings, each measured rather than reasoned about, and each contrary to
 * the obvious approach.
 *
 * 1. SCREEN SPACE, NEVER DATA SPACE. The textbook method judges a bend in data
 *    coordinates and is blind to the window: the same parabola in a square
 *    window and a 20:1 tall window gives an IDENTICAL 5.86-degree data angle
 *    for two pictures that look nothing alike. A student sees pixels.
 *
 * 2. FLATNESS IS DISTANCE, NOT ANGLE. Every reference implementation measures
 *    the ANGLE between successive chords. Approaching tan's pole two chords
 *    differ by 2.4 DEGREES — under any threshold — while the midpoint sits 97
 *    PIXELS from the chord. Near-vertical chords have nearly the same direction
 *    whatever they do. The PERPENDICULAR DISTANCE from the midpoint to the
 *    chord IS the visible error; bounding it bounds the picture. That change
 *    took tan from 100px to 3.5px and REDUCED the point count.
 *
 * 3. A POLE NEEDS A DIVERGENCE TEST. The obvious "did y jump more than the
 *    viewport" never fires: samples straddling tan's pole are only 439px apart,
 *    so the pole passes for a steep bend and gets drawn through. Instead ask
 *    whether the MIDPOINT IS FAR OUTSIDE its neighbours' range — 1536px outside
 *    a 439px span at a pole, 3px outside 5px on ordinary curve.
 *
 * 4. OFF-SCREEN CULLING STOPS REFINEMENT, IT DOES NOT INTENSIFY IT. Treating
 *    "far from the viewport" as a reason to subdivide harder produced 28,717
 *    points on a steep parabola.
 *
 * And one structural rule: SAMPLING AND SPLITTING ARE SEPARATE PASSES. Cutting
 * the path from inside the recursion left the parent call appending to the
 * wrong run.
 */
export function sampleFn(fn, x0, x1, vp, opts = {}) {
  const { tolerance = 0.25, maxDepth = 14, seedPx = 12, budget = 4000 } = opts;
  const { sx, sy, height } = vp;

  // A generous band around the viewport. Beyond it nothing is visible, so there
  // is nothing to refine — the line leaves the picture either way.
  const TOP = -height;
  const BOT = height * 2;
  const off = (p) => p[1] < TOP || p[1] > BOT;

  const at = (x) => {
    const y = fn(x);
    return Number.isFinite(y) ? [sx(x), sy(y)] : null;
  };

  const pts = [];              // { p, cut } in x order; cut = break BEFORE this
  let spent = 0;
  const add = (p, cut) => { pts.push({ p, cut }); spent += 1; };

  const refine = (xa, pa, xb, pb, depth) => {
    if (depth <= 0 || spent > budget) return;
    // Both ends off the SAME edge: nothing between them can be seen.
    if (off(pa) && off(pb) && (pa[1] < TOP) === (pb[1] < TOP)) return;

    const xm = (xa + xb) / 2;
    const pm = at(xm);
    if (!pm) { add(null, true); return; }          // undefined: break, no point

    // FINDING 3 — divergence, not a jump threshold.
    const span = Math.abs(pa[1] - pb[1]);
    const outside = Math.max(0,
      Math.max(pa[1], pb[1]) - pm[1],
      pm[1] - Math.min(pa[1], pb[1]));
    if (outside > span * 2 + height / 4) {
      refine(xa, pa, xm, pm, depth - 1);
      add(pm, false);
      add(null, true);                              // the break sits AT the pole
      refine(xm, pm, xb, pb, depth - 1);
      return;
    }

    // FINDING 2 — perpendicular distance from the midpoint to the chord.
    const dx = pb[0] - pa[0];
    const dy = pb[1] - pa[1];
    const len = Math.hypot(dx, dy);
    const dist = len === 0
      ? Math.hypot(pm[0] - pa[0], pm[1] - pa[1])
      : Math.abs(dy * (pm[0] - pa[0]) - dx * (pm[1] - pa[1])) / len;
    if (dist <= tolerance) return;

    refine(xa, pa, xm, pm, depth - 1);
    add(pm, false);
    refine(xm, pm, xb, pb, depth - 1);
  };

  // SEED DENSITY IS A SCREEN DISTANCE. Seeds exist so a narrow feature is not
  // stepped over entirely; refinement does the rest. A fixed count spent 34
  // points on EVERY piece of a five-piece graph, including a horizontal segment
  // that needs two.
  const spanPx = Math.abs(sx(x1) - sx(x0));
  const seed = Math.max(2, Math.min(80, Math.round(spanPx / seedPx)));

  let prevX = null;
  let prevP = null;
  for (let i = 0; i <= seed; i += 1) {
    const x = x0 + ((x1 - x0) * i) / seed;
    const p = at(x);
    if (!p) { add(null, true); prevX = null; prevP = null; continue; }
    if (prevP) refine(prevX, prevP, x, p, maxDepth);
    add(p, false);
    prevX = x;
    prevP = p;
  }

  return toRuns(pts);
}

/**
 * ── SAMPLING A PARAMETRIC CURVE ─────────────────────────────────────────────
 *
 * The same shape as `sampleFn`, but the interval is in `t` and each sample
 * yields a POINT rather than a height. Everything after — flatness, splitting,
 * drawing — is shared.
 */
export function sampleParam(spec, vp, opts = {}) {
  const { tolerance = 0.25, maxDepth = 12, seed = 64, budget = 4000 } = opts;
  const { sx, sy } = vp;

  const at = (t) => {
    const q = spec.at(t);
    if (!q || !Number.isFinite(q[0]) || !Number.isFinite(q[1])) return null;
    return [sx(q[0]), sy(q[1])];
  };

  const pts = [];
  let spent = 0;
  const add = (p, cut) => { pts.push({ p, cut }); spent += 1; };

  const refine = (ta, pa, tb, pb, depth) => {
    if (depth <= 0 || spent > budget) return;
    const tm = (ta + tb) / 2;
    const pm = at(tm);
    if (!pm) { add(null, true); return; }
    const dx = pb[0] - pa[0];
    const dy = pb[1] - pa[1];
    const len = Math.hypot(dx, dy);
    const dist = len === 0
      ? Math.hypot(pm[0] - pa[0], pm[1] - pa[1])
      : Math.abs(dy * (pm[0] - pa[0]) - dx * (pm[1] - pa[1])) / len;
    if (dist <= tolerance) return;
    refine(ta, pa, tm, pm, depth - 1);
    add(pm, false);
    refine(tm, pm, tb, pb, depth - 1);
  };

  let prevT = null;
  let prevP = null;
  for (let i = 0; i <= seed; i += 1) {
    const t = spec.from + ((spec.to - spec.from) * i) / seed;
    const p = at(t);
    if (!p) { add(null, true); prevT = null; prevP = null; continue; }
    if (prevP) refine(prevT, prevP, t, p, maxDepth);
    add(p, false);
    prevT = t;
    prevP = p;
  }

  const runs = toRuns(pts);
  // A CLOSED SHAPE MUST MEET ITSELF. Sampling t from 0 to 2pi lands the last
  // point on the first, but floating point can leave a hairline gap.
  if (spec.closed && runs.length === 1 && runs[0].length > 2) runs[0].push(runs[0][0]);
  return runs;
}

/**
 * ONE PASS: a flagged point list becomes runs.
 *
 * Separate from sampling on purpose — see the note on `sampleFn`. A run of one
 * point draws nothing, and a run narrower than a pixel is the sliver that
 * appears beside a pole: `1/x` produces two real runs and a 2-point fragment
 * spanning x = -0.005 to 0. Both are dropped.
 */
export function toRuns(pts, minPx = 1) {
  const runs = [];
  let run = [];
  const close = () => {
    if (run.length > 1) {
      // MEASURE THE WIDTH, NOT THE HEIGHT. A sliver beside a pole is narrow in
      // x and TALL in y — the curve is racing off the screen — so testing the
      // height lets exactly the fragment through that this exists to drop.
      //
      // AND MEASURE THE WHOLE RUN, NOT FIRST-TO-LAST. A CLOSED SHAPE ends where
      // it began, so first-to-last width is zero and a circle would be
      // discarded as a sliver.
      let lo = run[0][0];
      let hi = run[0][0];
      for (const [x] of run) { if (x < lo) lo = x; if (x > hi) hi = x; }
      if (hi - lo >= minPx) runs.push(run);
    }
    run = [];
  };
  for (const { p, cut } of pts) {
    if (cut) { close(); continue; }
    run.push(p);
  }
  close();
  return runs;
}

/**
 * ── ONE CURVE, WHATEVER KIND, INTO RUNS OF SCREEN POINTS ────────────────────
 *
 * The one place the three sampling kinds are chosen between. Everything above
 * is reachable on its own; everything below draws.
 */
export function curveRuns(curve, vp, opts) {
  if (!curve || typeof curve !== 'object') return [];

  // GIVEN POINTS. No sampling at all — the points ARE the run.
  if (curve.type === 'polyline') {
    const pts = (curve.points || [])
      .filter((q) => Array.isArray(q) && Number.isFinite(q[0]) && Number.isFinite(q[1]))
      .map((q) => [vp.sx(q[0]), vp.sy(q[1])]);
    return pts.length > 1 ? [pts] : [];
  }

  // A vertical line is not a function and needs no sampler either.
  if (curve.type === 'verticalLine') {
    if (!Number.isFinite(curve.x)) return [];
    const px = vp.sx(curve.x);
    return [[[px, 0], [px, vp.height]]];
  }

  // PARAMETRIC.
  if (PARAM[curve.type]) {
    const spec = PARAM[curve.type](curve);
    if (!spec.at || !Number.isFinite(spec.from) || !Number.isFinite(spec.to)) return [];
    return sampleParam(spec, vp, opts);
  }

  // y = f(x), including piecewise — which yields several intervals. A piecewise
  // may ALSO hold pieces that are not functions of x; those go through this
  // same dispatch, one level down.
  return [
    ...intervals(curve, vp).flatMap((s) => sampleFn(s.fn, s.x0, s.x1, vp, opts)),
    ...nonFunctionPieces(curve, vp, opts),
  ];
}

/**
 * ── ENDPOINT DOTS, FROM THE STRICTNESS ──────────────────────────────────────
 *
 * `closedLeft` and `closedRight` say whether each end of a piece is INCLUDED.
 * A filled dot means closed, a hollow one open — and both fall straight out of
 * the spec, with no limits taken and nothing inferred.
 *
 * That is the case a plotting library makes you hand-place, and it is free here
 * because the strictness is in the data.
 *
 * A dot is only drawn at an INTERNAL boundary. Where a piece runs to the edge
 * of the window (`from` or `to` is null) there is no endpoint to mark.
 */
export function endpointDots(curve, vp, minGap = 1.5) {
  if (!curve || curve.type !== 'piecewise') return [];

  // Gather every boundary a piece claims, with the value it takes there.
  const marks = [];
  for (const p of curve.pieces || []) {
    const fn = p && p.curve && CURVE[p.curve.type];
    if (!fn) continue;
    const f = fn(p.curve);
    for (const [x, filled] of [[p.from, !!p.closedLeft], [p.to, !!p.closedRight]]) {
      if (x == null || !Number.isFinite(x)) continue;
      // THE WINDOW EDGE IS NOT AN ENDPOINT. A piece may name the edge
      // explicitly — `from: -5` on a window starting at -5 — rather than using
      // null, and a dot there marks the edge of the PICTURE, not a
      // discontinuity in the curve.
      const edge = vp.stepX / 1000;
      if (x <= vp.xmin + edge || x >= vp.xmax - edge) continue;
      const y = f(x);
      if (!Number.isFinite(y)) continue;
      marks.push({ x, cx: vp.sx(x), cy: vp.sy(y), filled });
    }
  }

  // ── A CONTINUOUS JOIN GETS NO DOT ──────────────────────────────────────────
  //
  // Two pieces meeting at x each claim that boundary, so a five-piece chain
  // produces EIGHT marks at four joins. Where the graph simply carries on, both
  // land on the SAME POINT — and drawing a filled and a hollow circle on top of
  // one another leaves the result to z-order.
  //
  // A dot means a DISCONTINUITY. Where the two sides agree, the curve is
  // continuous and there is nothing to mark; where they differ, both are drawn,
  // which is the filled-and-hollow pair a textbook shows at a jump.
  const byX = new Map();
  for (const m of marks) {
    const k = m.x.toFixed(6);
    if (!byX.has(k)) byX.set(k, []);
    byX.get(k).push(m);
  }
  const out = [];
  for (const group of byX.values()) {
    if (group.length === 1) { out.push(group[0]); continue; }
    const lo = Math.min(...group.map((m) => m.cy));
    const hi = Math.max(...group.map((m) => m.cy));
    if (hi - lo < minGap) continue;                 // continuous: no dot
    out.push(...group);
  }
  return out;
}

/** A run of screen points as an SVG path. */
export function toPath(run) {
  if (!run || run.length < 2) return '';
  return `M ${run.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}`;
}

/**
 * ── THE GRID ────────────────────────────────────────────────────────────────
 *
 * Gridlines at (max - min) / 10 on each axis, and the axes themselves where
 * they fall inside the window — a window that does not contain zero has no axis
 * to draw, which is common on a page showing only the first quadrant.
 *
 * THE GRIDLINES MATTER MOST ON BLANK AXES. An empty box with two lines through
 * it looks broken; an empty GRID looks like graph paper, which is what is
 * printed on the page.
 */
export function grid(vp) {
  const lines = { v: [], h: [] };
  const first = (min, step) => Math.ceil(min / step) * step;
  for (let x = first(vp.xmin, vp.stepX); x <= vp.xmax + 1e-9; x += vp.stepX) {
    if (Math.abs(x) < vp.stepX / 1e6) continue;            // the axis, not a gridline
    lines.v.push({ px: vp.sx(x), value: x });
  }
  for (let y = first(vp.ymin, vp.stepY); y <= vp.ymax + 1e-9; y += vp.stepY) {
    if (Math.abs(y) < vp.stepY / 1e6) continue;
    lines.h.push({ py: vp.sy(y), value: y });
  }
  return {
    ...lines,
    axisX: vp.ymin <= 0 && vp.ymax >= 0 ? vp.sy(0) : null,
    axisY: vp.xmin <= 0 && vp.xmax >= 0 ? vp.sx(0) : null,
  };
}

/**
 * ── EVERYTHING A PLOT NEEDS TO DRAW ─────────────────────────────────────────
 *
 * One call from the card. Returns geometry only — no colours, no elements. The
 * INK is decided by the caller from the element's `origin`, so a student's own
 * drawn curve can be green while printed axes are not, and this module stays
 * pure.
 */
export function plot(data, width, height, opts) {
  if (!data || !Array.isArray(data.window) || data.window.length !== 4) return null;
  if (!data.window.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  const [xmin, ymax, xmax, ymin] = data.window;
  if (!(xmax > xmin) || !(ymax > ymin)) return null;        // a degenerate window

  const vp = viewport(data.window, width, height);
  const curves = Array.isArray(data.curves) ? data.curves : [];
  return {
    vp,
    grid: grid(vp),
    // `curves: []` IS A REAL RESULT — blank printed axes for a student to draw
    // on. It returns a grid and no paths, which is exactly right.
    paths: curves.flatMap((c) => curveRuns(c, vp, opts).map(toPath)).filter(Boolean),
    dots: curves.flatMap((c) => endpointDots(c, vp)),
  };
}
